import { Fragment, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, IconButton, validationUtils } from 'basic-components';
import { Clear, Info } from 'basic-components/icons';

import { PickersUtils } from '../Utils';
import Modal from './Modal';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import '../Picker.scss';

const EMPTY_ARRAY = [];
const EMPTY_OBJ = {};
const InsightPicker = ({
  disabled = false,
  size = 'normal',
  tooltipProps = EMPTY_OBJ,
  optionalText = undefined,
  onChange,
  name,
  defaultValue = undefined,
  validators = EMPTY_ARRAY,
  required = false,
  noError = false,
  ref
}) => { 
  const [isOpen, setIsOpen] = useState(false),
        [value, setValue] = useState(),
        [isValid, setValid] = useState(true),
        [errorMessage, setErrorMessage] = useState(undefined),
        [isOverflowed, setIsOverflowed] = useState(false),

        refErrorMessage = useRef(null),

        openModal = useCallback(() => setIsOpen(true), [setIsOpen]),
        closeModal = useCallback(() => setIsOpen(false), [setIsOpen]),
        clearValue = useCallback(() => !disabled && setValue(""), [disabled]),
        validate = useCallback(v => validationUtils.validateNoState(v, validators, name, required, setValid, setErrorMessage), [name, required, validators])
    
  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  useEffect(() => {
    value !== undefined && onChange(value, { name })
  }, [value, name, onChange])

  useEffect(() => {
    validate(typeof value === 'object' ? value.name : value)
  }, [value, validate])

  useImperativeHandle(ref, () => ({ 
    validateForForm: () => validate(typeof value === 'object' ? value.name : value),
    isValid: () => isValid
  }))
  useEffect(() => {
    if(refErrorMessage.current && refErrorMessage.current.clientWidth < refErrorMessage.current.scrollWidth) {
      setIsOverflowed(true)
    } else {
      setIsOverflowed(false)
    }
  }, [isValid, errorMessage])

  return (
    <div className="ng-common-picker">
      <div className={classNames("ng-common-picker__row",  { "ng-common-picker__row--choosen": value !== "" && value !== undefined && value !== " " }, { "ng-common-picker__row--not-choosen": value === "" || value === undefined || value === " " })}>
        <If condition={value !== "" && value !== undefined  && value !== " "}>
          <Then>
            {() => (
              <Fragment>
                <div className="ng-common-picker__row--choosen__top">
                  <p className="ng-common-picker__row--choosen__top__name">{value?.name}</p>
                  <IconButton
                      className={classNames(
                        "ng-common-picker__row--choosen__top__clear",
                        { "ng-common-picker__row--choosen__top__clear--is-disabled": disabled }
                      )}
                      onClick={clearValue}
                  >
                    <Clear/>
                  </IconButton>
                </div>
                <p className="ng-common-picker__row--choosen__optional">{`Group: ${value?.groupName || "Unknown"}`}</p>
              </Fragment>
            )}
          </Then>
          <Else>
            {() => (
              <Button
                  disabled={disabled}
                  onClick={openModal}
                  size={size}
                  type="button"
                  variant="text"
              >
                Select Insight
              </Button>
            )}
          </Else>
        </If>
        <When condition={!noError}>
          {() => (
            <div className={classNames("ng-common-picker__error-container", { "ng-common-picker__error-container--is-none": isValid })}>
              <p ref={refErrorMessage}>
                {errorMessage}
              </p>
              <When condition={isOverflowed}>
                {() => PickersUtils.getErrorTooltip(errorMessage, "ng-common-picker__error-container__more")}
              </When>
            </div>
          )}
        </When>
      </div>
      <When condition={Object.keys(tooltipProps || {})?.length > 0}>
        {() => (
          <div 
              className="ng-common-picker__tooltip-container"
              {...tooltipProps}
          >
            <Info/>
          </div>
        )}
      </When>
      <When condition={isOpen}>
        {() => (
          <Modal
              closeModal={closeModal}
              defaultValue={defaultValue}
              onSubmit={setValue}
              optionalText={optionalText}
          />
        )}
      </When>
    </div>
  )
}


InsightPicker.propTypes = {
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  noError: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  optionalText: PropTypes.string,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'normal', 'large']),
  tooltipProps: PropTypes.object,
  validators: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string.isRequired,
    validator: PropTypes.func.isRequired
  }))
}

InsightPicker.displayName = 'InsightPicker'

export default InsightPicker;
